import React, { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import cx from 'classnames';
import {
	DashboardSettingsLocalizedRequest,
	DashboardSettingsUpdateRequest,
	SUPPORTED_MEDIA_FORMAT,
} from '@joc/api-gateway/lib/api-client';

import { ReactComponent as UploadIcon } from 'assets/icons/upload-lg.svg';
import { ReactComponent as PencilSvg } from 'assets/image/pencil-light.svg';
import { API } from 'core/API';
import {
	createEmptyLocalizedMap,
	LocalizedStringsMap,
	SUPPORTED_LANGUAGES,
	SupportedLanguageKey,
} from 'core/constants';
import useThunkDispatch from 'core/customHooks/useThunkDispatch';
import { useLocales } from 'hooks/locales';
import { SET_ERROR } from 'redux/actions-types';
import { getLiveScreen, updateLiveScreen } from 'redux/dashboard-service/action';
import { selectorGetLiveScreen } from 'redux/dashboard-service/selector';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import { isVideo } from 'shared/components/MarketingComponent/Statuses/helpers';
import mediaInputStyles from 'shared/components/MediaInput/MediaInput.module.scss';
import { SnackbarSuccess } from 'shared/components/_Snackbars';

import { LiveScreenTextModal, LiveScreenTextsValue, TextSectionKey } from './LiveScreenTextModal';
import styles from './LiveScreenSettings.module.scss';

const areMapsEqual = (a: LocalizedStringsMap, b: LocalizedStringsMap): boolean =>
	SUPPORTED_LANGUAGES.every(lang => (a[lang.key] || '').trim() === (b[lang.key] || '').trim());

export const LiveScreenSettings: FC = () => {
	const dispatch = useThunkDispatch();
	const liveScreenInfo = useSelector(selectorGetLiveScreen);
	const { localeIdByKey, languageKeyByLocaleId } = useLocales();

	const inputRef = useRef<HTMLInputElement>(null);

	const [adminMediaItems, setAdminMediaItems] = useState<(string | File)[]>([]);
	const [originalAdminMediaItems, setOriginalAdminMediaItems] = useState<(string | File)[]>([]);
	const [topText, setTopText] = useState<LocalizedStringsMap>(createEmptyLocalizedMap);
	const [bottomText, setBottomText] = useState<LocalizedStringsMap>(createEmptyLocalizedMap);
	const [savedTopText, setSavedTopText] = useState<LocalizedStringsMap>(createEmptyLocalizedMap);
	const [savedBottomText, setSavedBottomText] = useState<LocalizedStringsMap>(createEmptyLocalizedMap);

	const [topPreviewLang, setTopPreviewLang] = useState<SupportedLanguageKey>('en');
	const [bottomPreviewLang, setBottomPreviewLang] = useState<SupportedLanguageKey>('en');
	const [modalSection, setModalSection] = useState<TextSectionKey | null>(null);

	const [showError, setShowError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isUploading, setIsUploading] = useState(false);
	const [isCongrats, setIsCongrats] = useState(false);

	useEffect(() => {
		const nextTop = createEmptyLocalizedMap();
		const nextBottom = createEmptyLocalizedMap();

		if (liveScreenInfo) {
			if (liveScreenInfo.title) {
				nextTop.en = liveScreenInfo.title;
			}
			if (liveScreenInfo.subTitle) {
				nextBottom.en = liveScreenInfo.subTitle;
			}

			if (Array.isArray(liveScreenInfo.dashboardLocalized)) {
				liveScreenInfo.dashboardLocalized.forEach(item => {
					const langKey = languageKeyByLocaleId[item.localeId];
					if (langKey) {
						if (langKey === 'en') {
							nextTop.en = item.titleLocalized || nextTop.en;
							nextBottom.en = item.subTitleLocalized || nextBottom.en;
						} else {
							nextTop[langKey] = item.titleLocalized || '';
							nextBottom[langKey] = item.subTitleLocalized || '';
						}
					}
				});
			}

			const items = (liveScreenInfo.mediaPathes || []).filter(Boolean);
			setAdminMediaItems(items);
			setOriginalAdminMediaItems(items);
		} else {
			setAdminMediaItems([]);
			setOriginalAdminMediaItems([]);
		}

		setTopText(nextTop);
		setBottomText(nextBottom);
		setSavedTopText({ ...nextTop });
		setSavedBottomText({ ...nextBottom });
	}, [liveScreenInfo, languageKeyByLocaleId]);

	const isMediaDirty = useMemo(() => {
		const hasChanges = adminMediaItems.some((item, index) => {
			const original = originalAdminMediaItems[index];
			return item !== original;
		});
		return hasChanges || adminMediaItems.length !== originalAdminMediaItems.length;
	}, [adminMediaItems, originalAdminMediaItems]);

	const isTextDirty = useMemo(() => {
		return !areMapsEqual(topText, savedTopText) || !areMapsEqual(bottomText, savedBottomText);
	}, [topText, savedTopText, bottomText, savedBottomText]);

	const isValid = useMemo(() => {
		return Boolean(topText.en.trim()) && Boolean(bottomText.en.trim()) && adminMediaItems.length > 0;
	}, [topText.en, bottomText.en, adminMediaItems.length]);

	const isNeedSave = (isMediaDirty || isTextDirty) && isValid;

	const settingsMediaPreviewUrl = useMemo(() => {
		const mediaItem = adminMediaItems[0];
		if (!mediaItem) {
			return '';
		}
		if (mediaItem instanceof File) {
			return URL.createObjectURL(mediaItem);
		}
		return mediaItem;
	}, [adminMediaItems]);

	useEffect(() => {
		return () => {
			if (settingsMediaPreviewUrl.startsWith('blob:')) {
				URL.revokeObjectURL(settingsMediaPreviewUrl);
			}
		};
	}, [settingsMediaPreviewUrl]);

	const handleOpenMedia = () => {
		if (inputRef.current) {
			inputRef.current.click();
		}
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();

		const selectedFile = event.target.files?.[0];
		if (!selectedFile) {
			return;
		}

		if (selectedFile.size > 10 * 1024 * 1024) {
			setShowError(true);
			setErrorMessage('File size exceeds the 10 MB limit.');
			event.target.value = '';
			return;
		}

		const supportedTypes = [
			SUPPORTED_MEDIA_FORMAT.Video_mp4,
			SUPPORTED_MEDIA_FORMAT.Image_jpeg,
			SUPPORTED_MEDIA_FORMAT.Image_png,
			SUPPORTED_MEDIA_FORMAT.Image_bmp,
		];
		const supportedExtensions = ['.mp4', '.jpeg', '.jpg', '.png', '.bmp'];
		const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

		const isValidType = supportedTypes.includes(selectedFile.type as SUPPORTED_MEDIA_FORMAT);
		const isValidExtension = supportedExtensions.includes(fileExtension);

		if (!isValidType && !isValidExtension) {
			setShowError(true);
			setErrorMessage('Please choose an image (PNG, JPEG, BMP) or MP4 video file smaller than 10 MB.');
			event.target.value = '';
			return;
		}

		setShowError(false);
		setErrorMessage('');
		setAdminMediaItems([selectedFile]);
		event.target.value = '';
	};

	const handleRemoveMedia = (index: number) => {
		const updatedMedia = adminMediaItems.filter((_, i) => i !== index);
		setAdminMediaItems(updatedMedia);
	};

	const handleApplyTexts = (values: LiveScreenTextsValue) => {
		setTopText({ ...values.topText });
		setBottomText({ ...values.bottomText });
	};

	const handleSave = async () => {
		try {
			setIsUploading(true);
			setShowError(false);
			setErrorMessage('');

			const uploadedMediaPathes = await Promise.all(
				adminMediaItems.map(async item => {
					if (item instanceof File) {
						const response = await API.uploadFile({
							data: item,
							fileName: item.name,
						});
						return `${process.env.REACT_APP_S3_API}/${response.fileName}`;
					}
					if (typeof item === 'string') {
						return item;
					}
					return '';
				})
			);

			const mediaPathes = uploadedMediaPathes.filter(Boolean);
			const englishTop = topText.en.trim();
			const englishBottom = bottomText.en.trim();

			const dashboardLocalized: DashboardSettingsLocalizedRequest[] = [];

			SUPPORTED_LANGUAGES.forEach(lang => {
				const localeId = localeIdByKey[lang.key];
				const titleLocalized = (topText[lang.key] || '').trim();
				const subTitleLocalized = (bottomText[lang.key] || '').trim();

				if (lang.required) {
					dashboardLocalized.push(
						DashboardSettingsLocalizedRequest.fromJS({
							localeId,
							titleLocalized: englishTop,
							subTitleLocalized: englishBottom,
						})
					);
				} else if (titleLocalized || subTitleLocalized) {
					dashboardLocalized.push(
						DashboardSettingsLocalizedRequest.fromJS({
							localeId,
							titleLocalized,
							subTitleLocalized,
						})
					);
				}
			});

			const payload = DashboardSettingsUpdateRequest.fromJS({
				dashboardLocalized,
				...(mediaPathes.length ? { mediaPathes } : {}),
				interval: liveScreenInfo?.interval || 10,
			});

			await dispatch(updateLiveScreen(payload));
			await dispatch(getLiveScreen());
			setIsCongrats(true);
		} catch (error: any) {
			dispatch({ type: SET_ERROR, payload: { state: true, message: error.message } });
		} finally {
			setIsUploading(false);
		}
	};

	const renderTextCard = (
		sectionKey: TextSectionKey,
		title: string,
		valuesMap: LocalizedStringsMap,
		previewLang: SupportedLanguageKey,
		// eslint-disable-next-line no-unused-vars
		onSelectLang: (lang: SupportedLanguageKey) => void
	) => {
		const rawValue = (valuesMap[previewLang] || '').trim();
		const englishFallback = (valuesMap.en || '').trim();
		const displayValue = rawValue || englishFallback;
		const isUsingFallback = previewLang !== 'en' && !rawValue && Boolean(englishFallback);
		const isRtl = previewLang === 'he' && Boolean(rawValue);

		return (
			<div className={styles.card}>
				<div className={styles.card__header}>
					<h3 className={styles.card__title}>{title}</h3>
					<button type="button" className={styles.card__editBtn} onClick={() => setModalSection(sectionKey)}>
						<PencilSvg className={styles.card__editIcon} />
						<span>Edit</span>
					</button>
				</div>

				<div className={styles.langBadges}>
					{SUPPORTED_LANGUAGES.map(lang => {
						const hasTranslation = Boolean((valuesMap[lang.key] || '').trim());
						const isSelected = previewLang === lang.key;

						return (
							<button
								key={lang.key}
								type="button"
								className={cx(styles.langBadge, {
									[styles.langBadge_filled]: hasTranslation,
									[styles.langBadge_active]: isSelected,
								})}
								onClick={() => onSelectLang(lang.key)}
								title={
									hasTranslation
										? `${lang.label}: configured`
										: `${lang.label}: using English fallback`
								}
							>
								<span>{lang.key.toUpperCase()}</span>
								{hasTranslation && (
									<span
										className={cx(styles.langBadge__dot, {
											[styles.langBadge__dot_active]: isSelected,
										})}
									/>
								)}
							</button>
						);
					})}
				</div>

				<div
					className={styles.previewBox}
					onClick={() => setModalSection(sectionKey)}
					role="button"
					tabIndex={0}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setModalSection(sectionKey);
						}
					}}
				>
					<p
						className={cx(styles.previewBox__text, {
							[styles.previewBox__text_rtl]: isRtl,
							[styles.previewBox__text_empty]: !displayValue,
						})}
					>
						{displayValue || 'Click to add text...'}
					</p>
					{isUsingFallback && (
						<span className={styles.previewBox__fallback}>Fallback: English (default)</span>
					)}
				</div>
			</div>
		);
	};

	const hasSettingsMedia = adminMediaItems.length > 0;

	return (
		<>
			<input
				onChange={handleFileChange}
				accept="video/mp4,image/jpeg,image/png,image/bmp"
				ref={inputRef}
				hidden
				type="file"
				disabled={isUploading}
			/>

			<div className={styles.settingsPanel}>
				<div className={styles.settingsHeader}>
					<h2 className={styles.settingsTitle}>Live Screen Settings</h2>
					<ButtonDefault
						title={isUploading ? 'Saving...' : 'Save'}
						icon={isUploading ? <CircularProgress size={18} thickness={4} color="inherit" /> : undefined}
						classList={['primary', 'lg']}
						clickHandler={handleSave}
						disabled={!isNeedSave || isUploading}
						parentClassName={styles.settingsSave}
					/>
				</div>

				{showError && errorMessage ? (
					<div className={styles.error}>
						<span className={styles.error__text}>{errorMessage}</span>
					</div>
				) : null}

				<div className={styles.settingsGrid}>
					<div className={styles.settingsColumn}>
						<div className={styles.card}>
							<div className={styles.card__header}>
								<h3 className={styles.card__title}>Upload New Media</h3>
							</div>
							<div className={styles.mediaSection}>
								{hasSettingsMedia ? (
									<div className={mediaInputStyles.mediaInput}>
										<div
											className={mediaInputStyles.mediaInput__image_hover}
											onClick={() => handleRemoveMedia(0)}
										>
											{isVideo(adminMediaItems[0]) ? (
												// eslint-disable-next-line jsx-a11y/media-has-caption
												<video
													className={mediaInputStyles.mediaInput__image}
													src={settingsMediaPreviewUrl}
													muted
												/>
											) : (
												<img
													className={mediaInputStyles.mediaInput__image}
													src={settingsMediaPreviewUrl}
													alt="Upload New Media"
												/>
											)}
										</div>
										<button
											type="button"
											className={styles.mediaChangeBtn}
											onClick={handleOpenMedia}
											disabled={isUploading}
										>
											Upload new media
										</button>
									</div>
								) : (
									<div className={mediaInputStyles.mediaInput}>
										<UploadIcon
											className={mediaInputStyles.mediaInput__image}
											onClick={isUploading ? undefined : handleOpenMedia}
										/>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className={styles.settingsColumn}>
						{renderTextCard('topText', 'Top Line Text', topText, topPreviewLang, setTopPreviewLang)}
						{renderTextCard(
							'bottomText',
							'Bottom Line Text',
							bottomText,
							bottomPreviewLang,
							setBottomPreviewLang
						)}
					</div>
				</div>
			</div>

			<LiveScreenTextModal
				isOpen={modalSection !== null}
				initialSection={modalSection || 'topText'}
				topText={topText}
				bottomText={bottomText}
				onClose={() => setModalSection(null)}
				onApply={handleApplyTexts}
			/>

			<SnackbarSuccess
				open={isCongrats}
				onClose={setIsCongrats}
				title="Success!"
				message="Information has been updated."
				width={320}
			/>
		</>
	);
};
